import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, House, ChartLine, HousePlus, Sparkle, DoorOpen, DoorClosed, Search, BarChart as BarChartIcon, MousePointer, Calculator, User, LogOut, Key, HelpCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { 
  Sidebar, 
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar
} from "@/components/ui/sidebar";
import { SidebarToggleButton, SidebarCollapseIndicator, MobileSidebarContent } from "@/components/ui/SidebarToggle";
import { useIsMobile } from '@/hooks/use-mobile';
import MobileNavbar from './MobileNavbar';

interface AppSidebarProps {
  activePage?: 'home' | 'analysis' | 'dashboard' | 'dashboard-aluguel' | 'dashboard-sairam-ultima-semana' | 'dashboard-entraram-ultima-semana' | 'admin' | 'agency-analytics' | null;
}

/**
 * AppSidebar component that is shared across multiple pages
 */
const AppSidebar: React.FC<AppSidebarProps> = ({ activePage }) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const isMobile = useIsMobile();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const sidebarContent = (
    <>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Menu Principal</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Dashboard" isActive={activePage === 'home'}>
                  <a href="/home">
                    <House size={18} />
                    <span>Início</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Análise de Propriedades" isActive={activePage === 'analysis'}>
                  <a href="/analisar-imovel">
                    <Search size={18} />
                    <span>Análise de Propriedades</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Dashboard de Venda" isActive={activePage === 'dashboard'}>
                  <a href="/dashboard-venda">
                    <HousePlus size={18} />
                    <span>Dashboard de Venda</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Dashboard de Aluguel" isActive={activePage === 'dashboard-aluguel'}>
                  <a href="/dashboard-aluguel">
                    <Key size={18} />
                    <span>Dashboard de Aluguel</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Saíram do Mercado" isActive={activePage === 'dashboard-sairam-ultima-semana'}>
                  <a href="/dashboard-sairam-ultima-semana">
                    <DoorOpen size={18} />
                    <span>Saíram do Mercado</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Entraram no Mercado" isActive={activePage === 'dashboard-entraram-ultima-semana'}>
                  <a href="/dashboard-entraram-ultima-semana">
                    <DoorClosed size={18} />
                    <span>Entraram no Mercado</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Análise de Imobiliárias" isActive={activePage === 'agency-analytics'}>
                  <a href="/agency-analytics">
                    <Building size={18} />
                    <span>Análise de Imobiliárias</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>

            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        
        <SidebarGroup>
          <SidebarGroupLabel>Ferramentas - em breve</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Assistentes Virtuais" disabled>
                  <span>
                    <Sparkle size={18} />
                    <span>Assistentes Virtuais</span>
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Calculadoras" disabled>
                  <span>
                    <Calculator size={18} />
                    <span>Calculadoras</span>
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="mt-auto">
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Perfil" isActive={activePage === 'admin'}>
                  <a href="/admin">
                    <User size={18} />
                    <span>Perfil</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Ajuda">
                  <a href="https://airtable.com/appLF47Bh7ma9i85l/pagICdNi6PkBXm2Cy/form" target="_blank" rel="noopener noreferrer">
                    <HelpCircle size={18} />
                    <span>Ajuda</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              
              <SidebarMenuItem>
                <SidebarMenuButton onClick={handleSignOut} tooltip="Sair">
                  <LogOut size={18} />
                  <span>Sair</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </>
  );

  return (
    <>
      {isMobile && <MobileNavbar />}
      
      <Sidebar>
        <SidebarHeader className="pt-6">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2 min-w-0">
              <ChartLine className="h-6 w-6 md:h-7 md:w-7 text-blue-main flex-shrink-0" />
              <h1 className="text-lg md:text-xl font-bold text-blue-main">Broker Base</h1>
            </div>
            <SidebarToggleButton />
          </div>
        </SidebarHeader>
        
        {sidebarContent}
      </Sidebar>
      
      {/* Mobile sidebar content */}
      {isMobile && <MobileSidebarContent>{sidebarContent}</MobileSidebarContent>}
      
      <SidebarCollapseIndicator />
    </>
  );
};

export default AppSidebar;
